import React from "react";
import { useState } from "react";
import {
    Modal,
    Box,
    Typography,
    Backdrop,
    TextField,
    Button,
    CircularProgress,
} from "@mui/material";
import { ErrorOutline, UploadFile, GroupAdd } from "@mui/icons-material";
import { AnimatePresence, motion } from "framer-motion";
import axiosInstance from "../../../api/axiosInstance";
import useNotification from "../../common/Notification";
import WarningMessage from "../../common/WarningMessage";
import base_url from "../../../constants";

const style = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    maxWidth: 820,
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: 3,
    p: 4,
};

const WhatsappBusinessGroupCreate = ({ opening, closing, open, onSuccess }) => {
    const [backdropOpen, setBackdropOpen] = useState(false);
    const [sizeError, setSizeError] = useState(false);
    const [step, setStep] = useState(1);
    const showNotification = useNotification();
    
    const handleClose = () => {
        closing();
        setStep(1);
    };

    const [formData, setFormData] = useState({
        name: "",
        file: null,
    });

    const [errors, setErrors] = useState({
        name: false,
        file: false,
    });

    const handleTextChange = (e) => {
        setFormData({ ...formData, name: e.target.value });
        setErrors({ ...errors, name: false });
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        setFormData({ ...formData, file: selectedFile });
        setErrors({ ...errors, file: false });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setSizeError(false);

        const isNameValid = formData.name.trim() !== "";
        const isFileValid = formData.file !== null;

        if (!isNameValid || !isFileValid) {
            setErrors({
                name: !isNameValid,
                file: !isFileValid,
            });
            return;
        }

        const newFormData = new FormData();
        newFormData.append("group_name", formData.name);
        newFormData.append("file", formData.file);

        try {
            setBackdropOpen(true);
            const res = await axiosInstance.post(
                `${base_url}/whatsapp-business/groups/create`,
                newFormData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            showNotification("Group Created Successfully", "success");
            handleClose();
            setFormData({
                name: "",
                file: null,
            });
            if (onSuccess) onSuccess();
        } catch (e) {
            const msg = e.response?.data?.message;
            if (e.response?.status === 422 && msg && String(msg).includes("10,000")) {
                setSizeError(true);
            }
            if (e.response?.status === 400) {
                showNotification(
                    e.response?.data?.message || "Failed to create group",
                    "error"
                );
                return;
            }
            if (e.response?.status === 409) {
                showNotification(
                    e.response?.data?.message || "Group already exists",
                    "error"
                );
                return;
            }
            if (e.response?.status === 422) {
                showNotification(
                    e.response?.data?.message || "Some duplicates found",
                    "warning"
                );
                return;
            }
            showNotification("Something went wrong please try again!", "error");
        } finally {
            setBackdropOpen(false);
        }
    };

    return (
        <div>
            <Modal
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                open={open}
                onClose={handleClose}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{
                    backdrop: {
                        timeout: 500,
                    },
                }}
            >
                <AnimatePresence>
                    {open && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            style={{
                                position: "fixed",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "rgba(15,23,42,0.55)",
                            }}
                        >
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.9 }}
                                transition={{ duration: 0.3 }}
                            >
                                <Box sx={style}>
                                    <Box
                                        component="form"
                                        onSubmit={handleSubmit}
                                        sx={{
                                            width: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 2.5,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                            }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Box
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: "50%",
                                                        backgroundColor: "rgba(16,105,110,0.08)",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                    }}
                                                >
                                                    <GroupAdd sx={{ fontSize: 20, color: "primary.main" }} />
                                                </Box>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 700,
                                                        letterSpacing: "-0.02em",
                                                    }}
                                                >
                                                    Create Group
                                                </Typography>
                                            </Box>
                                            <Button
                                                onClick={handleClose}
                                                size="small"
                                                sx={{ minWidth: "auto" }}
                                            >
                                                ✕
                                            </Button>
                                        </Box>

                                        {step === 1 ? (
                                            <>
                                                {/* Info / warning for group size */}
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "flex-start",
                                                        gap: 1,
                                                        p: 1.5,
                                                        borderRadius: 2,
                                                        border: "1px solid rgba(248,113,113,0.7)",
                                                        backgroundColor: "rgba(254,242,242,1)",
                                                        mt: 2,
                                                    }}
                                                >
                                                    <ErrorOutline
                                                        sx={{ fontSize: 20, color: "#b91c1c", mt: 0.25 }}
                                                    />
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ color: "#b91c1c", fontWeight: 500 }}
                                                    >
                                                        Groups containing more than 10,000 numbers are not
                                                        allowed.
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        alignItems: "center",
                                                        textAlign: "center",
                                                        mt: 3,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ mb: 1.5, color: "text.secondary" }}
                                                    >
                                                        Set your group name to start organising contacts.
                                                    </Typography>
                                                    <Box
                                                        component="img"
                                                        src="/images/create-group-img.png"
                                                        alt="Create group illustration"
                                                        sx={{
                                                            maxWidth: 180,
                                                            height: "auto",
                                                            objectFit: "contain",
                                                            mb: 2,
                                                        }}
                                                    />
                                                </Box>
                                                <TextField
                                                    label="Group Name"
                                                    fullWidth
                                                    size="small"
                                                    variant="outlined"
                                                    placeholder="Enter group name"
                                                    value={formData.name}
                                                    onChange={handleTextChange}
                                                    error={errors.name}
                                                    helperText={errors.name ? "Name is required" : ""}
                                                    sx={{ mt: 2 }}
                                                />
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "flex-end",
                                                        mt: 3,
                                                    }}
                                                >
                                                    <Button
                                                        variant="contained"
                                                        component={motion.button}
                                                        whileHover={{ scale: 1.02 }}
                                                        whileTap={{ scale: 0.98 }}
                                                        sx={{
                                                            borderRadius: 2,
                                                            px: 3,
                                                            backgroundColor: "#111827",
                                                            "&:hover": {
                                                                backgroundColor: "#020617",
                                                            },
                                                        }}
                                                        onClick={() => {
                                                            const isNameValid = formData.name.trim() !== "";
                                                            if (!isNameValid) {
                                                                setErrors((prev) => ({
                                                                    ...prev,
                                                                    name: true,
                                                                }));
                                                                return;
                                                            }
                                                            setStep(2);
                                                        }}
                                                    >
                                                        Next
                                                    </Button>
                                                </Box>
                                            </>
                                        ) : (
                                            <>
                                                {/* Static note for 10,000 contacts */}
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "flex-start",
                                                        gap: 1,
                                                        p: 1.5,
                                                        borderRadius: 2,
                                                        border: "1px solid rgba(248,113,113,0.7)",
                                                        backgroundColor: "rgba(254,242,242,1)",
                                                        mt: 2,
                                                    }}
                                                >
                                                    <ErrorOutline
                                                        sx={{ fontSize: 20, color: "#b91c1c", mt: 0.25 }}
                                                    />
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ color: "#b91c1c", fontWeight: 500 }}
                                                    >
                                                        Groups containing more than 10,000 numbers are not
                                                        allowed.
                                                    </Typography>
                                                </Box>

                                                {/* Illustration + upload */}
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        alignItems: "center",
                                                        mt: 3,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 64,
                                                            height: 64,
                                                            borderRadius: "50%",
                                                            backgroundColor: "rgba(17,24,39,0.04)",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            mb: 1.5,
                                                        }}
                                                    >
                                                        <UploadFile sx={{ fontSize: 32, color: "#111827" }} />
                                                    </Box>
                                                    <Button
                                                        type="button"
                                                        variant="outlined"
                                                        component="label"
                                                        sx={{ borderRadius: 2, mb: 1 }}
                                                    >
                                                        Choose Excel / CSV File
                                                        <input
                                                            type="file"
                                                            hidden
                                                            accept=".xls, .xlsx, .csv"
                                                            onChange={handleFileChange}
                                                        />
                                                    </Button>
                                                    {formData.file && (
                                                        <Typography variant="body2">
                                                            Selected file: {formData.file.name}
                                                        </Typography>
                                                    )}
                                                    {errors.file && (
                                                        <Typography
                                                            color="error"
                                                            variant="body2"
                                                            sx={{ mt: 0.5 }}
                                                        >
                                                            Please select an Excel file
                                                        </Typography>
                                                    )}
                                                    <Typography
                                                        sx={{
                                                            mt: 1.5,
                                                            fontSize: "12px",
                                                        }}
                                                    >
                                                        <span style={{ fontWeight: "bold" }}>Note: </span>
                                                        Upload an Excel or CSV sheet with a column named{" "}
                                                        <code>phone_number</code>.
                                                    </Typography>
                                                </Box>

                                                {/* Submit button bottom-right */}
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        mt: 3,
                                                    }}
                                                >
                                                    <Button
                                                        variant="text"
                                                        onClick={() => setStep(1)}
                                                        sx={{ textTransform: "none" }}
                                                    >
                                                        Back
                                                    </Button>
                                                    <Button
                                                        type="submit"
                                                        variant="contained"
                                                        component={motion.button}
                                                        whileHover={{ scale: 1.02 }}
                                                        whileTap={{ scale: 0.98 }}
                                                        sx={{
                                                            borderRadius: 2,
                                                            px: 3,
                                                            backgroundColor: "#111827",
                                                            "&:hover": {
                                                                backgroundColor: "#020617",
                                                            },
                                                            position: "relative",
                                                        }}
                                                        disabled={backdropOpen}
                                                    >
                                                        Submit
                                                        {backdropOpen ? (
                                                            <CircularProgress
                                                                size={24}
                                                                sx={{
                                                                    position: "absolute",
                                                                    top: "50%",
                                                                    left: "50%",
                                                                    marginTop: "-12px",
                                                                    marginLeft: "-12px",
                                                                }}
                                                            />
                                                        ) : null}
                                                    </Button>
                                                </Box>
                                            </>
                                        )}
                                    </Box>
                                </Box>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Modal>
        </div>
    );
};

export default WhatsappBusinessGroupCreate;

